import React, { Suspense } from "react";

import { shouldPolyfill } from "@formatjs/intl-getcanonicallocales/should-polyfill";
import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { CompatRouter } from "react-router-dom-v5-compat";

import { isLocal } from "./app/config/featureFlags";
import { EnvironmentProvider } from "./app/providers/EnvironmentProvider";
import OnlineStatusProvider from "./app/providers/OnlineStatusProvider";
import TelemetryProvider from "./app/providers/TelemetryProvider";
import WindowFocusProvider from "./app/providers/WindowFocusProvider";
import { FullScreenLoader } from "./app/ui/loaders/fullScreenLoader";
import ScrollToTop from "./app/ui/shared/ScrollToTop";
import { initFeatureFlagOverrides } from "./app/util/featureFlagsUtilities";
import initMuiLicense from "./app/util/initMuiLicense";
import { lazyWithRetry } from "./app/util/lazyWithRetry";
import { environment } from "./environments/environment";

import "./initI18next";

import "./styles.css";

Sentry.init({
  dsn: isLocal ? "" : "https://8c04c6de44a9418a8b57700e85210311@o1083388.ingest.sentry.io/6093050",
  integrations: [new Sentry.BrowserTracing(), new Sentry.Replay()],
  environment: "unknown",
  release: "unknown",
  enabled: environment.deployed,
  // capture replays on 2% of sessions - let's see how this works out
  replaysSessionSampleRate: 0.01,
  // capture replay on all errors
  replaysOnErrorSampleRate: 1.0,
  // Set tracesSampleRate to 0.5 to capture 50%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.5,
});

void (async () => {
  await initFeatureFlagOverrides();
})();

const App = lazyWithRetry(() => import("./app/app"));

initMuiLicense();

let root: ReturnType<typeof createRoot> | null = null;

const renderApp = () => {
  if (!root) {
    const container = document.getElementById("root");
    if (!container) {
      throw new Error("Root element not found");
    }
    root = createRoot(container);
  }
  root.render(
    <React.StrictMode>
      <Suspense fallback={<FullScreenLoader />}>
        <BrowserRouter>
          <ScrollToTop />
          <CompatRouter>
            <OnlineStatusProvider />
            <WindowFocusProvider />
            <EnvironmentProvider />
            <TelemetryProvider>
              <App />
            </TelemetryProvider>
          </CompatRouter>
        </BrowserRouter>
      </Suspense>
    </React.StrictMode>,
  );
};

const start = async () => {
  // Check if platform already supports Intl.getCanonicalLocales
  // https://caniuse.com/mdn-javascript_builtins_intl_supportedvaluesof
  // Global support at the time of writing 89.63%
  if (shouldPolyfill()) {
    await import("@formatjs/intl-getcanonicallocales/polyfill");
  }
  renderApp();
};

void start();
